<template>
  <v-dialog :model-value="show" max-width="1200px" @update:model-value="$emit('update:show', $event)">
    <v-card>
      <v-card-title>
        <div>
          <h2>Select Equipment</h2>
          <p class="text-subtitle-2 text-grey">
            For {{ request?.student_name }} - {{ getSchoolName(request?.school) }}
          </p>
        </div>
      </v-card-title>
      <v-card-text>
        <div v-if="loading" class="text-center pa-4">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </div>

        <div v-else-if="!bestMatch" class="text-center pa-4">
          <v-alert type="warning" variant="tonal">
            <div class="text-body-1 mb-2">
              <strong>No equipment meets the requirements for this request.</strong>
            </div>
            <div class="text-body-2">
              This request requires equipment with a minimum rating of {{ request?.minimum_rating || 1 }},
              but there is no available equipment that meets this requirement.
            </div>
          </v-alert>
        </div>

        <div v-else>
          <!-- Best Match Section -->
          <div class="mb-4">
            <div class="d-flex align-center mb-2">
              <h3 class="text-h6">Best Match</h3>
              <v-chip class="ml-2" :color="getMatchScoreColor(bestMatch.matchScore)" size="small">
                {{ bestMatch.matchScore }}% Match
              </v-chip>
            </div>

            <!-- Warning for imperfect matches -->
            <v-alert v-if="bestMatch.matchScore < 100" type="warning" variant="tonal" class="mb-3">
              <div class="text-body-2">
                Tasks highlighted in <v-chip size="x-small" color="error" class="mx-1">red</v-chip> are requested by the student but not supported by this equipment.
                Tasks in <v-chip size="x-small" color="success" class="mx-1">green</v-chip> are both requested and supported.
                Note: Equipment may still be capable of performing red-highlighted tasks, but it may not be ideal for them.
              </div>
            </v-alert>

            <!-- Best Match Card -->
            <v-card variant="outlined" class="best-match-card">
              <v-card-text>
                <v-row>
                  <v-col cols="12" md="6">
                    <div class="mb-3">
                      <div class="d-flex align-center mb-2">
                        <v-chip color="secondary" size="small" class="mr-2">
                          {{ bestMatch.equipment.equipment_type }}
                        </v-chip>
                        <v-chip
                          :color="getRatingColor(bestMatch.equipment.rating)"
                          size="small"
                          variant="flat"
                        >
                          Rating: {{ bestMatch.equipment.rating || '-' }}
                        </v-chip>
                      </div>
                      <div class="text-h6">
                        <strong>{{ bestMatch.equipment.manufacturer }}</strong>
                        {{ bestMatch.equipment.model }}
                      </div>
                      <div class="text-caption text-grey">
                        Asset Tag: {{ bestMatch.equipment.asset_tag || 'N/A' }}
                      </div>
                    </div>

                    <v-row dense>
                      <v-col cols="6">
                        <div class="text-caption text-grey">CPU</div>
                        <div>{{ bestMatch.equipment.cpu || 'N/A' }}</div>
                      </v-col>
                      <v-col cols="6">
                        <div class="text-caption text-grey">RAM</div>
                        <div>{{ bestMatch.equipment.ram || 'N/A' }}</div>
                      </v-col>
                      <v-col cols="6">
                        <div class="text-caption text-grey">Storage</div>
                        <div>{{ bestMatch.equipment.storage || 'N/A' }}</div>
                      </v-col>
                      <v-col cols="6">
                        <div class="text-caption text-grey">GPU</div>
                        <div>{{ bestMatch.equipment.gpu || 'N/A' }}</div>
                      </v-col>
                    </v-row>
                  </v-col>

                  <v-col cols="12" md="6">
                    <div v-if="request?.needed_for && request.needed_for.length > 0">
                      <div class="text-caption text-grey mb-2">Task Compatibility</div>
                      <div class="d-flex flex-wrap ga-2">
                        <v-chip
                          v-for="task in request.needed_for"
                          :key="task"
                          size="small"
                          :color="bestMatch.supportedTasks.includes(task) ? 'success' : 'error'"
                          :variant="bestMatch.supportedTasks.includes(task) ? 'flat' : 'tonal'"
                        >
                          <v-icon v-if="bestMatch.supportedTasks.includes(task)" start size="small">mdi-check</v-icon>
                          <v-icon v-else start size="small">mdi-close</v-icon>
                          {{ formatTaskName(task) }}
                        </v-chip>
                      </div>
                    </div>

                    <div v-if="bestMatch.equipment.comments" class="mt-3">
                      <div class="text-caption text-grey">Comments</div>
                      <div class="text-body-2">{{ bestMatch.equipment.comments }}</div>
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn
                  color="success"
                  variant="elevated"
                  @click="$emit('claim', bestMatch.equipment)"
                >
                  <v-icon start>mdi-hand-extended</v-icon>
                  Claim This Equipment
                </v-btn>
              </v-card-actions>
            </v-card>
          </div>

          <!-- Other Options Section -->
          <div v-if="otherOptions.length > 0" class="mt-4">
            <v-divider class="mb-4"></v-divider>
            <v-btn
              @click="showOtherOptions = !showOtherOptions"
              variant="outlined"
              color="primary"
              block
              class="mb-3"
            >
              <v-icon start>{{ showOtherOptions ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
              {{ showOtherOptions ? 'Hide' : 'See' }} Other Options ({{ otherOptions.length }})
            </v-btn>

            <v-expand-transition>
              <div v-show="showOtherOptions">
                <InventoryTable
                  :equipment="otherOptions.map(m => m.equipment)"
                  :loading="false"
                  :request-mode="true"
                  :request-data="request"
                  :items-per-page="10"
                  @claim="$emit('claim', $event)"
                  @unclaim="$emit('unclaim', $event)"
                />
              </div>
            </v-expand-transition>
          </div>
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="grey" @click="$emit('update:show', false)">Cancel</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
/* eslint-disable no-undef */
import { ref, computed, watch } from 'vue'
import InventoryTable from '@/components/inventory/InventoryTable.vue'
import { useEquipmentMatching, type Equipment, type Request } from '@/composables/useEquipmentMatching'

interface School {
  id: string
  _id?: string
  name: string
}

interface Props {
  show: boolean
  request: Request | null
  equipment: Equipment[]
  loading?: boolean
  schools: School[]
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  equipment: () => [],
  schools: () => []
})

defineEmits(['update:show', 'claim', 'unclaim'])

const { findBestMatch, sortEquipmentByMatch, formatTaskName } = useEquipmentMatching()

const showOtherOptions = ref(false)

const sortedMatches = computed(() => {
  if (!props.request || !props.equipment || props.equipment.length === 0) return []
  const matches = sortEquipmentByMatch(props.equipment, props.request)
  console.log('Sorted matches:', matches)
  return matches
})

const bestMatch = computed(() => {
  if (sortedMatches.value.length === 0) return null
  const best = sortedMatches.value[0]
  console.log('Best match:', best)
  return best
})

const otherOptions = computed(() => {
  if (sortedMatches.value.length <= 1) {
    console.log('No other options, sorted matches length:', sortedMatches.value.length)
    return []
  }
  const others = sortedMatches.value.slice(1)
  console.log('Other options:', others.length)
  return others
})

const getSchoolName = (schoolId: string | undefined) => {
  if (!schoolId) return 'N/A'
  const school = props.schools.find((s: School) => s.id === schoolId || s._id === schoolId)
  return school ? school.name : schoolId
}

const getRatingColor = (rating: number | null) => {
  if (!rating) return 'grey'
  if (rating >= 8) return 'success'
  if (rating >= 6) return 'light-green'
  if (rating >= 4) return 'lime'
  return 'yellow-darken-2'
}

const getMatchScoreColor = (score: number) => {
  if (score === 100) return 'success'
  if (score >= 75) return 'light-green'
  if (score >= 50) return 'warning'
  return 'error'
}

// Reset showOtherOptions when modal closes or request changes
watch(() => props.show, (newVal) => {
  if (!newVal) {
    showOtherOptions.value = false
  }
})

watch(() => props.request?.id, () => {
  showOtherOptions.value = false
})
</script>

<style scoped>
.best-match-card {
  border: 2px solid rgba(76, 175, 80, 0.3);
  background-color: rgba(76, 175, 80, 0.05);
}
</style>
